// ./src/utils/api/EmailTemplateService.ts
import { gql } from "@apollo/client";
import { ApiService } from "./ApiService";
import { OpResult } from "@sdflc/api-helpers";
import { Pagination, SortItem } from "./commonApiTypes";
import { User } from "./UserService";
import { EmailLayout } from "./EmailLayoutService";
import { SYSTEM_ACCOUNT_ID } from "@/config";

// GraphQL queries
const EMAIL_TEMPLATE_LIST_QUERY = gql`
  query EmailTemplateList(
    $filter: EmailTemplateFilter
    $params: PaginationAndSorting
  ) {
    emailTemplateList(filter: $filter, params: $params) {
      code
      data {
        id
        accountId
        emailLayoutId
        name
        description
        templateType
        subject
        htmlContent
        textContent
        fromName
        fromEmail
        replyToEmail
        isDefault
        status
        rights
        version
        createdBy
        updatedBy
        createdAt
        updatedAt
        userCreated {
          id
          firstName
          lastName
          email
        }
        userUpdated {
          id
          firstName
          lastName
          email
        }
        emailLayout {
          id
          name
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const EMAIL_TEMPLATE_GET_QUERY = gql`
  query EmailTemplateGet($id: ID) {
    emailTemplateGet(id: $id) {
      code
      data {
        id
        accountId
        emailLayoutId
        name
        description
        templateType
        subject
        htmlContent
        textContent
        fromName
        fromEmail
        replyToEmail
        isDefault
        status
        rights
        version
        createdBy
        updatedBy
        removedBy
        createdAt
        updatedAt
        userCreated {
          id
          firstName
          lastName
          email
        }
        userUpdated {
          id
          firstName
          lastName
          email
        }
        emailLayout {
          id
          name
          htmlContent
          textContent
          fromName
          fromEmail
          replyToEmail
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const EMAIL_TEMPLATE_GET_MANY_QUERY = gql`
  query EmailTemplateGetMany($ids: [ID]) {
    emailTemplateGetMany(ids: $ids) {
      code
      data {
        id
        accountId
        emailLayoutId
        name
        description
        templateType
        subject
        htmlContent
        textContent
        fromName
        fromEmail
        replyToEmail
        isDefault
        status
        rights
        version
        createdBy
        updatedBy
        createdAt
        updatedAt
        userCreated {
          id
          firstName
          lastName
          email
        }
        userUpdated {
          id
          firstName
          lastName
          email
        }
        emailLayout {
          id
          name
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const EMAIL_TEMPLATE_PREVIEW_QUERY = gql`
  query EmailTemplatePreview($id: ID, $sampleData: String) {
    emailTemplatePreview(id: $id, sampleData: $sampleData) {
      code
      data {
        id
        accountId
        emailLayoutId
        name
        description
        templateType
        subject
        rights
        htmlContent
        textContent
        fromName
        fromEmail
        replyToEmail
        isDefault
        status
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// GraphQL mutations
const EMAIL_TEMPLATE_CREATE_MUTATION = gql`
  mutation EmailTemplateCreate($params: EmailTemplateInput) {
    emailTemplateCreate(params: $params) {
      code
      data {
        id
        accountId
        emailLayoutId
        name
        description
        templateType
        subject
        htmlContent
        textContent
        fromName
        fromEmail
        replyToEmail
        isDefault
        status
        rights
        version
        createdBy
        updatedBy
        removedBy
        createdAt
        updatedAt
        userCreated {
          id
          firstName
          lastName
          email
        }
        userUpdated {
          id
          firstName
          lastName
          email
        }
        emailLayout {
          id
          name
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const EMAIL_TEMPLATE_UPDATE_MUTATION = gql`
  mutation EmailTemplateUpdate(
    $where: EmailTemplateWhereInput
    $params: EmailTemplateInput
  ) {
    emailTemplateUpdate(where: $where, params: $params) {
      code
      data {
        id
        accountId
        emailLayoutId
        name
        description
        templateType
        subject
        htmlContent
        textContent
        fromName
        fromEmail
        replyToEmail
        isDefault
        status
        rights
        version
        createdBy
        updatedBy
        removedBy
        createdAt
        updatedAt
        userCreated {
          id
          firstName
          lastName
          email
        }
        userUpdated {
          id
          firstName
          lastName
          email
        }
        emailLayout {
          id
          name
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const EMAIL_TEMPLATE_REMOVE_MUTATION = gql`
  mutation EmailTemplateRemove($where: EmailTemplateWhereInput) {
    emailTemplateRemove(where: $where) {
      code
      data {
        id
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const EMAIL_TEMPLATE_REMOVE_MANY_MUTATION = gql`
  mutation EmailTemplateRemoveMany($where: [EmailTemplateWhereInput]) {
    emailTemplateRemoveMany(where: $where) {
      code
      data {
        id
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const EMAIL_TEMPLATE_PUBLISH_MUTATION = gql`
  mutation EmailTemplatePublish($where: EmailTemplateWhereInput) {
    emailTemplatePublish(where: $where) {
      code
      data {
        id
        accountId
        emailLayoutId
        name
        description
        templateType
        subject
        htmlContent
        textContent
        fromName
        fromEmail
        replyToEmail
        isDefault
        status
        rights
        version
        createdBy
        updatedBy
        removedBy
        createdAt
        updatedAt
        userCreated {
          id
          firstName
          lastName
          email
        }
        userUpdated {
          id
          firstName
          lastName
          email
        }
        emailLayout {
          id
          name
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// Types
export interface EmailTemplate {
  id?: string | undefined;
  accountId?: string | undefined;
  emailLayoutId?: string | undefined;
  name?: string | undefined;
  description?: string | undefined;
  templateType?: string | undefined;
  subject?: string | undefined;
  htmlContent?: string | undefined;
  textContent?: string | undefined;
  fromName?: string | undefined;
  fromEmail?: string | undefined;
  replyToEmail?: string | undefined;
  isDefault?: boolean | undefined;
  status?: number | undefined;
  version?: number | undefined;
  createdBy?: string | undefined;
  updatedBy?: string | undefined;
  removedBy?: string | undefined;
  createdAt?: string | undefined;
  updatedAt?: string | undefined;
  userCreated?: User;
  userUpdated?: User;
  userRemoved?: User;
  emailLayout?: EmailLayout;
  rights?: string[];
}

export interface EmailTemplateInput {
  id?: string | null | undefined;
  accountId?: string | null | undefined;
  emailLayoutId?: string | null | undefined;
  name?: string | null | undefined;
  description?: string | null | undefined;
  templateType?: string | null | undefined;
  subject?: string | null | undefined;
  htmlContent?: string | null | undefined;
  textContent?: string | null | undefined;
  fromName?: string | null | undefined;
  fromEmail?: string | null | undefined;
  replyToEmail?: string | null | undefined;
  isDefault?: boolean | null | undefined;
  status?: number | null | undefined;
}

export interface EmailTemplateFilter {
  id?: string[] | null | undefined;
  accountId?: string[] | null | undefined;
  emailLayoutId?: string[] | null | undefined;
  name?: string[] | null | undefined;
  templateType?: string[] | null | undefined;
  isDefault?: boolean | null | undefined;
  status?: number[] | null | undefined;
  searchKeyword?: string | null | undefined;
}

export interface EmailTemplateWhereInput {
  id?: string | undefined;
}

// Common template types
export const TEMPLATE_TYPES = {
  NOTIFICATION: "notification",
  CONFIRMATION: "confirmation",
} as const;

export type TemplateType = (typeof TEMPLATE_TYPES)[keyof typeof TEMPLATE_TYPES];

/**
 * Check if an email template is a system template (read-only)
 */
export const isSystemTemplate = (template: EmailTemplate): boolean => {
  return template.accountId === SYSTEM_ACCOUNT_ID;
};

/**
 * Email Template service that handles all email template-related API calls
 * Supports full CRUD operations: list, get, getMany, create, update, remove, removeMany
 * Also supports preview functionality
 */
export class EmailTemplateService {
  /**
   * Get a list of email templates with optional filtering, pagination, and sorting
   */
  static async list(
    filter?: EmailTemplateFilter,
    pagination?: Pagination,
    sortBy?: SortItem[]
  ): Promise<OpResult> {
    const result = await ApiService.executeQuery(EMAIL_TEMPLATE_LIST_QUERY, {
      filter: filter || {},
      params: {
        pagination: pagination || undefined,
        sortBy: sortBy || undefined,
      },
    });
    return result;
  }

  /**
   * Get a single email template by ID
   */
  static async get(id: string): Promise<OpResult> {
    const result = await ApiService.executeQuery(EMAIL_TEMPLATE_GET_QUERY, {
      id: id,
    });
    return result;
  }

  /**
   * Get multiple email templates by IDs (preserves order)
   */
  static async getMany(ids: string[]): Promise<OpResult> {
    const result = await ApiService.executeQuery(
      EMAIL_TEMPLATE_GET_MANY_QUERY,
      {
        ids: ids,
      }
    );
    return result;
  }

  /**
   * Preview an email template with sample data
   * Returns the fully rendered template (with layout applied if configured)
   * @param id - Template ID to preview
   * @param sampleData - JSON string with sample variables
   */
  static async preview(id: string, sampleData?: string): Promise<OpResult> {
    const result = await ApiService.executeQuery(EMAIL_TEMPLATE_PREVIEW_QUERY, {
      id: id,
      sampleData: sampleData || null,
    });
    return result;
  }

  /**
   * Create a new email template
   */
  static async create(params: EmailTemplateInput): Promise<OpResult> {
    const result = await ApiService.executeMutation(
      EMAIL_TEMPLATE_CREATE_MUTATION,
      {
        params,
      }
    );
    return result;
  }

  /**
   * Update an existing email template
   */
  static async update(
    where: EmailTemplateWhereInput,
    params: EmailTemplateInput
  ): Promise<OpResult> {
    const result = await ApiService.executeMutation(
      EMAIL_TEMPLATE_UPDATE_MUTATION,
      {
        where,
        params,
      }
    );
    return result;
  }

  /**
   * Update an existing email layout
   */
  static async publish(where: EmailTemplateWhereInput): Promise<OpResult> {
    const result = await ApiService.executeMutation(
      EMAIL_TEMPLATE_PUBLISH_MUTATION,
      {
        where,
      }
    );
    return result;
  }

  /**
   * Remove/delete an email template
   */
  static async remove(id: string): Promise<OpResult> {
    const result = await ApiService.executeMutation(
      EMAIL_TEMPLATE_REMOVE_MUTATION,
      {
        where: { id },
      }
    );
    return result;
  }

  /**
   * Remove/delete multiple email templates
   */
  static async removeMany(ids: string[]): Promise<OpResult> {
    const where = ids.map((id) => ({ id }));
    const result = await ApiService.executeMutation(
      EMAIL_TEMPLATE_REMOVE_MANY_MUTATION,
      {
        where,
      }
    );
    return result;
  }
}
